import { logD } from "@blacknut/logging/dist";
import React, { useCallback, useState } from "react";
import { ReactComponent as Delete } from "../../../assets/keyboard/delete.svg";
import { ReactComponent as EnterArrow } from "../../../assets/keyboard/enter_arrow.svg";
import { ReactComponent as EnterSearch } from "../../../assets/keyboard/enter_search.svg";
import { ReactComponent as Shift } from "../../../assets/keyboard/shift.svg";
import { ReactComponent as Space } from "../../../assets/keyboard/space.svg";
import Button from "../../Button";
import ModalBase from "../ModalBase/ModalBase";
import styles from "./styles.module.scss";
import clsx from "clsx";

declare type KeyboardType = "numeric" | "simple" | "full";
const enum KeyboardDisplayType {
  "default" = "default",
  "shift" = "shift",
}
declare type EnterType = "search" | "enter";
const LOGGING_TAG = "VK";
const getSimpleLayoutFromLocale = (locale?: string) => {
  const _locale = locale || navigator.language;
// FIXME to much verbose use a memo  logD(LOGGING_TAG, "Using locale %o", _locale);

  // more to come

  return {
    default: [
      "1 2 3 4 5 6 7 8 9 0 - {bksp}",
      "a b c d e f g h i j k l m",
      "n o p q r s t u v w x y z",
      "{space} {enter}",
    ],
  };
};

const getFullLayoutFromLocale = (locale?: string) => {
  const _locale = locale || navigator.language;
// FIXME to much verbose use a memo  logD(LOGGING_TAG, "Using locale %o", _locale);
  // more to come
  return {
    default: [
      "` 1 2 3 4 5 6 7 8 9 0 - = {bksp}",
      "a b c d e f g h i j k l m n o",
      "{lock} p q r s t u v w x y z {shift}",
      "[ ] \\ ; ' , . /",
      "{space} {enter}",
    ],
    shift: [
      "~ ! @ # $ % ^ & * ( ) _ + {bksp}",
      "{tab} A B C D E F G H I J K L M",
      "{lock} N O P Q R S T U V W X {shift}",
      "Y Z < > ? { } | : {space} {enter}",
    ],
  };
};

export const getLayout = (
  type: KeyboardType = "simple",
  locale?: string
): { [key in KeyboardDisplayType]?: string[] } => {
  switch (type) {
    case "numeric": {
      return {
        default: ["1 2 3 4 5 6 7 8 9 0 {bksp} {enter}"],
      };
    }
    case "simple": {
      return getSimpleLayoutFromLocale(locale);
    }
    default:
      return getFullLayoutFromLocale(locale);
  }
};

const renderButtonContent = (e: string, enterType?: EnterType) => {
  switch (e) {
    case "{bksp}":
      return <Delete />;
    case "{space}":
      return <Space />;
    case "{shift}":
      return <Shift />;
    case "{enter}":
      return enterType === "search" ? <EnterSearch /> : <EnterArrow />;
  }
  return e;
};

const ButtonsRow = ({
  row,
  onClick,
  enterType,
}: {
  row: string;
  onClick: (key: string) => void;
  enterType?: EnterType;
}) => {
  const key = row.split(" ");

  return (
    <>
      {key.map((k) => {
        const _onClick = () => {
          onClick(k);
        };
        return (
          <Button
            key={k}
            onClick={_onClick}
            className={clsx(
              styles.button,
              k === "{bksp}" && styles.backspace,
              k === "{space}" && styles.space,
              k === "{enter}" && styles.enter,
              k === "{lock}" && styles.lock,
              k === "{shift}" && styles.shift,
              k === "{tab}" && styles.tab
            )}
            rippleClassName={styles.ripple}
            variant="secondary"
            testID={`btn-${k}`}
          >
            {renderButtonContent(k, enterType)}
          </Button>
        );
      })}
    </>
  );
};

const ButtonsRowMemo = React.memo(ButtonsRow, () => {
  return false;
});

const ModalBaseMemo = React.memo(ModalBase);
export interface VirtualKeyboardModalProps {
  onClose?: () => void;
  value?: string;
  setValue?: (s: string) => void;
  type?: KeyboardType;
  enterType?: EnterType;
  locale?: string;
  isOpen?: boolean;
  //required since VK modal does not use the standard modal behavior
  parentFocusPath?: string;
}

const VirtualKeyboardModal = ({
  onClose,
  value = "",
  setValue,
  type = "simple",
  enterType,
  locale,
  isOpen = true,
  parentFocusPath,
}: VirtualKeyboardModalProps) => {
  const [layout, setLayout] = useState<"default" | "shift" | "lock">(
    KeyboardDisplayType.default
  );

  const _onClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const rows = getLayout(type, locale)[layout === "lock" ? "shift" : layout];
  return (
    <ModalBaseMemo
      isOpen={isOpen}
      parentFocusPath={parentFocusPath}
      style={{
        overlay: { alignItems: "flex-end" },
        content: {
          borderRadius: 0,
          borderTop: "none",
          padding: 0,
          display: "flex",
          flex: 1,
          width: "unset",
        },
      }}
      focusableSectionClassName={styles.section}
      className={styles.container}
      onClose={_onClose}
    >
      <div
        className={clsx(
          styles.grid,
          type === "simple" && styles.simple,
          type === "numeric" && styles.numeric,
          type === "full" && styles.full,
          layout === "default" && styles.default,
          layout === "lock" && styles.lock,
          layout === "shift" && styles.shift
        )}
      >
        {rows?.map((e) => {
          const onClick = (s) => {
            switch (s) {
              case "{bksp}": {
                setValue?.(value.slice(0, -1));
                break;
              }
              case "{enter}": {
                onClose?.();
                break;
              }
              case "{space}": {
                setValue?.(value + " ");
                break;
              }
              case "{shift}": {
                setLayout(
                  layout == KeyboardDisplayType.default
                    ? KeyboardDisplayType.shift
                    : KeyboardDisplayType.default
                );
                break;
              }
              case "{lock}": {
                setLayout(
                  layout == KeyboardDisplayType.default
                    ? "lock"
                    : KeyboardDisplayType.default
                );
                break;
              }
              default:
                setValue?.(value + s);
                if (layout === "shift") {
                  setLayout(KeyboardDisplayType.default);
                }
            }
          };
          return (
            <ButtonsRowMemo
              row={e}
              key={e}
              onClick={onClick}
              enterType={enterType}
            />
          );
        })}
      </div>
    </ModalBaseMemo>
  );
};

export default VirtualKeyboardModal;
export { VirtualKeyboardModal };
